import { DatabaseContainerType, useAnalytics } from '@databases/analytics';
import { useRegisterCommentHandlers } from '@databases/comments/hooks';
import {
  useCommentActionsContext,
  useCommentStateContext
} from '@databases/comments/hooks/useCommentContext';
import { getThreadObjectFromId } from '@databases/comments/utils';
import { useSetupPanel } from '@databases/database-setup/useSetupPanel';
import { useEntryPanel } from '@databases/entry/useEntryPanel';
import { useLocale } from '@databases/intl';
import DatabaseLayout from '@databases/layout/DatabaseLayout';
import { getAppMode, isStandalone } from '@databases/modes';
import usePanel from '@databases/panel/usePanel';
import { PluginKey } from '@databases/plugins';
import { isPluginEnabled } from '@databases/plugins/helper';
import { useCallbackEffect } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { DatabaseLayoutType } from '@databases/types/database/view';
import { getViewSpecificOrderingExperimentValue } from '@databases/utils/database/view/view';
import { getValueId } from '@databases/utils/utils';
import { useCanEditFields } from '@databases/views/useCanEditFields';
import { useFilteredValues } from '@databases/views/useFilteredValues';
import useView from '@databases/views/useView';
import React, {
  type FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';

import { type DatabaseActionKey } from '../database/DatabaseActions.tsx';

import { Banners } from './Banners.tsx';
import DatabaseLayoutRenderer from './DatabaseLayoutRenderer.tsx';
import useCloseViewPanelKeyboardShortcut from './hooks/useCloseViewPanelKeyboardShortcut.ts';
import Toolbar from './Toolbar.tsx';

export interface DatabaseContentProps {
  contentId: string;
  isContentEditable?: boolean;
  actions?: DatabaseActionKey[];
  footer?: React.ReactNode;
}

/**
 * Renders a table that allows to edit database values using inline edit fields and to add fields and add entries to a
 * database.
 */
const DatabaseContent: FunctionComponent<DatabaseContentProps> = ({
  contentId,
  isContentEditable = false,
  actions,
  footer
}) => {
  const analyticsClient = useAnalytics();
  const locale = useLocale();
  const { showPanel } = usePanel();
  const { view } = useView();
  if (view.settings.fieldOrder || view.settings.entryOrder) {
    getViewSpecificOrderingExperimentValue({ fireExperimentExposure: true });
  }
  const [outerContainerRef, setOuterContainerRef] = useState<HTMLElement | null>(null);

  const { setOpenThread } = useCommentActionsContext();
  const { focusedCommentThreadId } = useCommentStateContext();

  const [operations, comments, shouldOpenPanelForFocusedComment] =
    defaultStoreUtils.useEqualSelector((state) => {
      const database = selectDatabaseDraftByContentId(state, {
        contentId,
        viewSettings: view.settings,
        includeDeleted: false
      });
      const comments = database.commentThreads;

      const focusedCommentThread = focusedCommentThreadId
        ? getThreadObjectFromId(focusedCommentThreadId, comments)
        : undefined;

      const shouldOpenPanelForFocusedComment = !!(
        focusedCommentThread?.entryId &&
        focusedCommentThread?.fieldId &&
        (!database.entries.includes(focusedCommentThread.entryId) ||
          !database.fields.includes(focusedCommentThread.fieldId) ||
          view.settings.layout.type === DatabaseLayoutType.GALLERY ||
          view.settings.layout.type === DatabaseLayoutType.BOARD)
      );

      return [database.operations, comments, shouldOpenPanelForFocusedComment] as const;
    });

  useSetupPanel({ contentId });
  useEntryPanel({ contentId });
  useFilteredValues({ contentId });

  const canEditContent = useMemo(
    () =>
      isContentEditable &&
      operations.updateEntries &&
      isPluginEnabled(PluginKey.EDIT_CONTENT),
    [isContentEditable, operations.updateEntries]
  );
  const { canEditFields, couldEditFields } = useCanEditFields({ contentId });

  useRegisterCommentHandlers({
    contentId,
    comments,
    canEditContent,
    shouldOpenPanelForFocusedComment
  });

  useEffect(() => {
    if (focusedCommentThreadId) {
      const commentThread = getThreadObjectFromId(focusedCommentThreadId, comments);
      if (commentThread && commentThread.entryId && commentThread.fieldId) {
        const { fieldId, entryId } = commentThread;
        setOpenThread({
          valueId: getValueId(entryId, fieldId),
          threadId: commentThread.thread.threadId,
          panel: shouldOpenPanelForFocusedComment
        });
        if (shouldOpenPanelForFocusedComment) {
          showPanel('EntryViewPanel', {
            entryId,
            isReadonly: !canEditContent
          });
        }
      }
    }
  }, [
    canEditContent,
    comments,
    focusedCommentThreadId,
    setOpenThread,
    shouldOpenPanelForFocusedComment,
    showPanel,
    view.settings.layout.type
  ]);

  useCallbackEffect(
    useCallback(async () => {
      await analyticsClient.sendDatabaseContentViewedEvent({
        contentId,
        databaseContainer: isStandalone()
          ? DatabaseContainerType.STANDALONE_DATABASE
          : DatabaseContainerType.EMBED_DATABASE,
        locale
      });
    }, [analyticsClient, contentId, locale])
  );

  useCloseViewPanelKeyboardShortcut();

  return (
    <DatabaseLayout
      banners={<Banners />}
      footer={footer}
      outerContainerRef={outerContainerRef}
      view={
        <DatabaseLayoutRenderer
          outerContainerRef={outerContainerRef}
          setOuterContainerRef={setOuterContainerRef}
          contentId={contentId}
          canEditContent={canEditContent}
          couldEditFields={couldEditFields}
          canEditFields={canEditFields}
          isContentEditable={isContentEditable}
        />
      }
      toolbar={
        <Toolbar
          contentId={contentId}
          actions={actions}
          canEditFields={canEditFields}
          canEditContent={canEditContent}
          mode={getAppMode()}
          referencing={false}
        />
      }
      isReadOnly={!canEditContent}
      layoutType={view.settings.layout.type}
    />
  );
};

export default DatabaseContent;
